<div class="pbl-head">
  <div>
    <h3>{{ mode === 'generated' ? 'Proposed backlog' : 'Example backlog' }}</h3>
    <p class="hint" *ngIf="mode === 'generated'">Drafted from the documents approved in the review step. Please review and validate the content before use.</p>
    <p class="hint" *ngIf="mode === 'examples'">Sample PBLs written from the L1 Production Execution documents. Generate to draft PBLs from your approved documents.</p>
    <p class="hint"><b>{{ items.length }}</b> requirements: <b>{{ items.length - matchedCount }}</b> net new, <b>{{ matchedCount }}</b> already in the SharePoint backlog.</p>
  </div>
  <div class="pbl-tally">
    <span class="t-ok"><i></i>{{ countOf('approved') }} approved</span>
    <span class="t-no"><i></i>{{ countOf('rejected') }} rejected</span>
    <span class="t-pend"><i></i>{{ countOf('pending') }} pending</span>
    <button type="button" class="regen-btn" (click)="regenerate()" [disabled]="generating">{{ mode === 'generated' ? 'Regenerate' : 'Reset' }}</button>
  </div>
</div>

<div class="gen-bar" [ngClass]="job?.status || 'idle'">
  <ng-container *ngIf="!generating">
    <button type="button" class="btn pri gen-btn" (click)="generate()" [disabled]="!!generateBlocker" [title]="generateBlocker">
      Generate from {{ approvedCount }} approved document{{ approvedCount === 1 ? '' : 's' }}
    </button>
    <button type="button" class="link-btn" *ngIf="mode === 'generated'" (click)="showExamples()">Show examples</button>
    <button type="button" class="link-btn" *ngIf="mode === 'examples' && job?.status === 'done' && job?.items?.length" (click)="showLastGenerated()">Show generated backlog</button>
    <span class="gen-note" *ngIf="generateBlocker">{{ generateBlocker }}</span>
    <span class="gen-note error" *ngIf="job?.status === 'failed'">{{ job?.message }}</span>
    <span class="gen-note" *ngIf="job?.status === 'done' && mode === 'generated' && job?.message">{{ job?.message }}</span>
  </ng-container>
  <ng-container *ngIf="generating && job">
    <span class="spin"></span>
    <span class="gen-stage">{{ job.stage }}…</span>
    <span class="gen-track"><i [style.width.%]="job.progress"></i></span>
    <span class="gen-pct">{{ job.progress }}%</span>
    <span class="gen-note">This can take a few minutes. You can keep reviewing the cards below.</span>
  </ng-container>
  <ul class="gen-warn" *ngIf="mode === 'generated' && job?.warnings?.length">
    <li *ngFor="let w of job?.warnings">{{ w }}</li>
  </ul>
</div>

<div class="dup-summary" *ngIf="dupState !== 'idle'" [ngClass]="dupState">
  <ng-container *ngIf="dupState === 'checking'">
    <span class="spin"></span> Checking the SharePoint backlog for duplicates…
  </ng-container>
  <ng-container *ngIf="dupState === 'error'">
    Duplicate check unavailable right now. You can still review and continue.
    <button type="button" class="link-btn" (click)="checkDuplicates()">Try again</button>
  </ng-container>
  <ng-container *ngIf="dupState === 'done' && dupCheck">
    <span>
      Checked against <b>{{ dupCheck.source.rows | number }}</b> approved items in {{ dupCheck.source.label || 'SharePoint PBL' }}:
      <span class="n likely" *ngIf="dupCheck.counts.likely">{{ dupCheck.counts.likely }} likely duplicate{{ dupCheck.counts.likely === 1 ? '' : 's' }}</span>
      <span class="n possible" *ngIf="dupCheck.counts.possible">{{ dupCheck.counts.possible }} possible duplicate{{ dupCheck.counts.possible === 1 ? '' : 's' }}</span>
      <span class="n new">{{ dupCheck.counts.netNew }} net new</span>
    </span>
    <span class="note">Warnings only. Nothing is blocked.</span>
    <span class="warn" *ngFor="let w of dupCheck.warnings">{{ w }}</span>
  </ng-container>
</div>

<div class="pbl-grid">
  <article class="pbl-card" *ngFor="let p of items" [ngClass]="statusOf(p)"
    [attr.tabindex]="p.reasoning ? 0 : null" [attr.aria-label]="p.id"
    [attr.aria-expanded]="p.reasoning ? selectedReasoningId === p.id : null"
    [attr.aria-controls]="p.reasoning ? 'reasoning-' + p.id : null"
    (click)="toggleReasoning(p, $event)" (keydown.enter)="toggleReasoning(p, $event)" (keydown.space)="toggleReasoning(p, $event)">
    <div class="meta">
      <select class="tag kind" [class.fit]="kindOf(p) === 'FIT'" [class.gap]="kindOf(p) === 'GAP'"
        [class.mixed]="kindOf(p) === 'Fit & Gap'" [attr.aria-label]="'Category for ' + p.id"
        [ngModel]="kindOf(p)" (ngModelChange)="changeKind(p, $event)">
        <option *ngFor="let kind of kinds" [ngValue]="kind">{{ kind }}</option>
      </select>
      <span class="tag" *ngIf="kindOf(p) !== 'FIT' && p.extension">{{ p.extension }}</span>
      <span class="tag" [ngClass]="p.match ? 'matched' : 'new'">{{ p.match ? 'Matched' : 'Net new' }}</span>
      <span class="tag">{{ p.priority }}</span>
      <span class="tag" *ngIf="p.tag">{{ p.tag }}</span>
      <span class="tag" *ngIf="p.subTeam">{{ p.subTeam }}</span>
      <span class="tag" *ngIf="p.l3">{{ p.l3 }}</span>
      <span class="status" *ngIf="statusOf(p) === 'approved'" [ngClass]="'approved'">✓ Approved</span>
      <span class="status" *ngIf="statusOf(p) === 'rejected'" [ngClass]="'rejected'">✕ Rejected</span>
      <span class="pid">{{ p.id }}</span>
    </div>
    <h4>{{ p.title || p.story }}</h4>
    <p class="story" *ngIf="p.title">{{ p.story }}</p>
    <div class="match" *ngIf="p.match">Existing PBL <b>{{ p.match.pblId }}</b> · about {{ p.match.overlap }}% overlap</div>
    <p class="ctx">{{ p.context }}</p>
    <div class="checks" *ngIf="p.checks?.length">
      <b>Needs review:</b>
      <ul><li *ngFor="let c of p.checks">{{ c }}</li></ul>
    </div>
    <div class="chips" *ngIf="p.chips.length">
      <span *ngFor="let c of p.chips">{{ c }}</span>
    </div>
    <div class="dup" *ngIf="dupOf(p) as d" [ngClass]="d.status">
      <div class="dup-title">
        ⚠ {{ d.matches[0].label }} of <b>{{ d.matches[0].pblId }}</b>
        <span class="score">{{ d.matches[0].score }}% match</span>
      </div>
      <div class="dup-match">“{{ d.matches[0].title }}”</div>
      <div class="dup-reason" *ngIf="d.matches[0].reason">{{ d.matches[0].reason }}</div>
      <div class="dup-more" *ngIf="d.matches.length > 1">
        Also similar:
        <span *ngFor="let m of d.matches.slice(1); let last = last" [title]="m.title">{{ m.pblId }} ({{ m.score }}%){{ last ? '' : ', ' }}</span>
      </div>
    </div>
    <details class="spec" *ngIf="p.acceptance?.length || p.sources?.length">
      <summary>PBL details</summary>
      <ng-container *ngIf="p.acceptance?.length">
        <b>Acceptance criteria</b>
        <ul><li *ngFor="let a of p.acceptance">{{ a }}</li></ul>
      </ng-container>
      <ng-container *ngIf="p.value"><b>Business value</b><p>{{ p.value }}</p></ng-container>
      <ng-container *ngIf="p.dependencies?.length">
        <b>Dependencies</b>
        <ul><li *ngFor="let d of p.dependencies">{{ d }}</li></ul>
      </ng-container>
      <ng-container *ngIf="p.assumptions?.length">
        <b>Assumptions</b>
        <ul><li *ngFor="let a of p.assumptions">{{ a }}</li></ul>
      </ng-container>
      <ng-container *ngIf="p.openQuestions?.length">
        <b>Open questions</b>
        <ul><li *ngFor="let q of p.openQuestions">{{ q }}</li></ul>
      </ng-container>
      <ng-container *ngIf="p.sources?.length">
        <b>Sources</b>
        <ul><li *ngFor="let s of p.sources">{{ s }}</li></ul>
      </ng-container>
    </details>
    <div class="acts">
      <button type="button" class="btn ok" [class.on]="statusOf(p) === 'approved'" (click)="decide(p, 'approved')">✓ Approve</button>
      <button type="button" class="btn no" [class.on]="statusOf(p) === 'rejected'" (click)="decide(p, 'rejected')">✕ Reject</button>
    </div>
    <div class="reasoning" *ngIf="p.reasoning && selectedReasoningId === p.id" [id]="'reasoning-' + p.id"><b>AI REASONING</b>{{ p.reasoning }}</div>
  </article>
</div>

<div class="step-nav">
  <button type="button" class="btn back-btn" (click)="back()">← Back</button>
  <span class="sp"></span>
  <button type="button" class="btn pri next-btn" (click)="next()">Continue to deck →</button>
</div>
